// src/components/ndr-management/NDRCallRecordingModal.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { API } from "../../utils/apiUrls";

export default function NDRCallRecordingModal({ ndr, onClose }) {
  const [activeTab, setActiveTab] = useState("list"); // "upload" or "list"
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [playingRecording, setPlayingRecording] = useState(null);
  const audioRef = useRef(null);

  const [uploadForm, setUploadForm] = useState({
    callRecordingFile: null,
    deliveryAttemptNumber: "",
    customerPhoneNumber: "",
    callDurationSeconds: "",
    callSummary: "",
    addressConfirmed: false,
    phoneNumberActive: false,
    courierReachedOut: false,
    additionalNotes: "",
  });

  useEffect(() => {
    if (activeTab === "list") {
      fetchRecordings();
    }
  }, [activeTab, ndr]);

  // Cleanup audio when modal closes
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  const fetchRecordings = async () => {
    setLoading(true);
    setError(null);
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");

      if (!authToken) {
        throw new Error("Authentication token not found");
      }

      const response = await axios.get(API.listNDRCallRecordings(), {
        params: {
          merchantId: merchantId || ndr.merchant_id,
          orderReferenceNumber: ndr.order_reference_number,
        },
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
        },
      });

      // Handle response structure
      const recordingsData = response.data?.recordings || response.data?.data?.recordings || [];
      setRecordings(Array.isArray(recordingsData) ? recordingsData : []);
    } catch (err) {
      console.error("Error fetching recordings:", err);
      setError(err.response?.data?.message || "Failed to fetch call recordings");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["audio/mpeg", "audio/mp3", "audio/m4a", "audio/wav", "audio/ogg", "audio/webm"];
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const validExtensions = ["mp3", "m4a", "wav", "ogg", "webm", "aac"];

      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        setError("Please select a valid audio file (mp3, m4a, wav, ogg, webm, aac)");
        e.target.value = "";
        return;
      }

      // Validate file size (max 50MB)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        setError("File size must be less than 50MB");
        e.target.value = "";
        return;
      }

      setUploadForm((prev) => ({ ...prev, callRecordingFile: file }));
      setError(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUploadForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!uploadForm.callRecordingFile) {
      setError("Please select a call recording file");
      return;
    }

    setUploading(true);

    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");

      if (!authToken) {
        throw new Error("Authentication token not found");
      }

      const formData = new FormData();
      formData.append("merchantId", merchantId || ndr.merchant_id);
      formData.append("orderReferenceNumber", ndr.order_reference_number);
      formData.append("callRecordingFile", uploadForm.callRecordingFile);

      // Add optional fields if provided
      if (uploadForm.deliveryAttemptNumber) {
        formData.append("deliveryAttemptNumber", uploadForm.deliveryAttemptNumber);
      }
      if (uploadForm.customerPhoneNumber) {
        formData.append("customerPhoneNumber", uploadForm.customerPhoneNumber);
      }
      if (uploadForm.callDurationSeconds) {
        formData.append("callDurationSeconds", uploadForm.callDurationSeconds);
      }
      if (uploadForm.callSummary) {
        formData.append("callSummary", uploadForm.callSummary);
      }
      formData.append("addressConfirmed", uploadForm.addressConfirmed.toString());
      formData.append("phoneNumberActive", uploadForm.phoneNumberActive.toString());
      formData.append("courierReachedOut", uploadForm.courierReachedOut.toString());
      if (uploadForm.additionalNotes) {
        formData.append("additionalNotes", uploadForm.additionalNotes);
      }

      const response = await axios.post(API.uploadNDRCallRecording(), formData, {
        headers: {
          Authorization: authToken,
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess("Call recording uploaded successfully!");
      
      // Reset form
      setUploadForm({
        callRecordingFile: null,
        deliveryAttemptNumber: "",
        customerPhoneNumber: "",
        callDurationSeconds: "",
        callSummary: "",
        addressConfirmed: false,
        phoneNumberActive: false,
        courierReachedOut: false,
        additionalNotes: "",
      });
      
      // Clear file input
      const fileInput = document.getElementById("callRecordingFile");
      if (fileInput) fileInput.value = "";

      // Refresh recordings list after a short delay
      setTimeout(() => {
        setActiveTab("list");
        fetchRecordings();
      }, 1500);
    } catch (err) {
      console.error("Error uploading recording:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to upload call recording. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const handlePlay = (recording) => {
    if (playingRecording?.stream_url === recording.stream_url) {
      // If clicking the same recording, toggle play/pause
      if (audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
        } else {
          audioRef.current.pause();
        }
      }
    } else {
      // Play new recording
      setPlayingRecording(recording);
      if (audioRef.current) {
        audioRef.current.src = recording.stream_url;
        audioRef.current.play();
      }
    }
  };

  const handleDownload = (recording) => {
    if (recording.download_url) {
      const link = document.createElement("a");
      link.href = recording.download_url;
      link.download = recording.call_recording_file_name || "recording";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const utcDate = parseISO(dateString);
      const istDate = toZonedTime(utcDate, "Asia/Kolkata");
      return format(istDate, "dd MMM yyyy, HH:mm");
    } catch {
      return dateString;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold">Call Recordings</h2>
            <p className="text-sm text-blue-100 mt-1">
              Order: {ndr.order_reference_number}
            </p>
          </div>
          <button
            onClick={() => {
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = "";
              }
              onClose();
            }}
            className="text-white hover:text-gray-200 text-3xl font-light leading-none transition-colors"
            disabled={uploading}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b bg-gray-50">
          <div className="flex">
            <button
              onClick={() => {
                setActiveTab("list");
                setError(null);
                setSuccess(null);
              }}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === "list"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                  />
                </svg>
                Recordings ({recordings.length})
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab("upload");
                setError(null);
                setSuccess(null);
              }}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === "upload"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                Upload Recording
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-red-500 mt-0.5 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-green-500 mt-0.5 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          )}

          {/* List Tab */}
          {activeTab === "list" && (
            <div>
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <p className="mt-4 text-gray-600">Loading recordings...</p>
                </div>
              ) : recordings.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <svg
                    className="w-16 h-16 text-gray-400 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                    />
                  </svg>
                  <p className="text-gray-600 text-lg">No recordings found</p>
                  <p className="text-gray-500 text-sm mt-2">
                    Upload a call recording to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recordings.map((recording, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-gray-900">
                              {recording.call_recording_file_name || "Recording"}
                            </h3>
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {recording.call_recording_file_format?.toUpperCase() || "AUDIO"}
                            </span>
                            {recording.interaction_status === "COMPLETED" && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                Completed
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                            <div>
                              <span className="font-medium">Size:</span>{" "}
                              {formatFileSize(recording.call_recording_file_size_bytes)}
                            </div>
                            <div>
                              <span className="font-medium">Attempt:</span>{" "}
                              {recording.delivery_attempt_number || "—"}
                            </div>
                            <div>
                              <span className="font-medium">Uploaded:</span>{" "}
                              {formatDate(recording.initiated_date_time)}
                            </div>
                            <div>
                              <span className="font-medium">By:</span>{" "}
                              {recording.called_by || "—"}
                            </div>
                          </div>

                          {/* Audio Player */}
                          {playingRecording?.stream_url === recording.stream_url && (
                            <div className="mb-3 bg-gray-50 rounded-lg p-3">
                              <audio
                                ref={audioRef}
                                controls
                                className="w-full"
                                onEnded={() => setPlayingRecording(null)}
                              >
                                <source src={recording.stream_url} type="audio/mp4" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>
                          )}

                        </div>

                        <div className="flex flex-col gap-2 ml-4">
                          <button
                            onClick={() => handlePlay(recording)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium"
                          >
                            {playingRecording?.stream_url === recording.stream_url ? (
                              <>
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                Pause
                              </>
                            ) : (
                              <>
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                Play
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleDownload(recording)}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm font-medium"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                              />
                            </svg>
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === "upload" && (
            <form onSubmit={handleUpload} className="space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Recording File <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-4h12m-4 4H12"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="callRecordingFile"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="callRecordingFile"
                          name="callRecordingFile"
                          type="file"
                          accept="audio/*,.mp3,.m4a,.wav,.ogg,.webm,.aac"
                          onChange={handleFileChange}
                          className="sr-only"
                          disabled={uploading}
                          required
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      MP3, M4A, WAV, OGG, WEBM, AAC up to 50MB
                    </p>
                    {uploadForm.callRecordingFile && (
                      <p className="text-sm text-green-600 font-medium mt-2">
                        Selected: {uploadForm.callRecordingFile.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Optional Fields Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Attempt Number
                  </label>
                  <input
                    type="number"
                    name="deliveryAttemptNumber"
                    value={uploadForm.deliveryAttemptNumber}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Phone Number
                  </label>
                  <input
                    type="tel"
                    name="customerPhoneNumber"
                    value={uploadForm.customerPhoneNumber}
                    onChange={handleInputChange}
                    placeholder="+919876543210"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Call Duration (seconds)
                  </label>
                  <input
                    type="number"
                    name="callDurationSeconds"
                    value={uploadForm.callDurationSeconds}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={uploading}
                  />
                </div>
              </div>

              {/* Call Summary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Call Summary
                </label>
                <textarea
                  name="callSummary"
                  value={uploadForm.callSummary}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Brief summary of the call conversation..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={uploading}
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="addressConfirmed"
                    checked={uploadForm.addressConfirmed}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={uploading}
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Address Confirmed
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="phoneNumberActive"
                    checked={uploadForm.phoneNumberActive}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={uploading}
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Phone Number Active
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="courierReachedOut"
                    checked={uploadForm.courierReachedOut}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={uploading}
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Courier Reached Out
                  </span>
                </label>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  name="additionalNotes"
                  value={uploadForm.additionalNotes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Any additional information about the call..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={uploading}
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="submit"
                  disabled={uploading || !uploadForm.callRecordingFile}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      Upload Recording
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={uploading}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

